import { json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { B } from '#app/components/typography/b.tsx'
import { Code } from '#app/components/typography/code.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Usage, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'B',
}

const component: componentProps = {
	name: 'B',
	shortName: 'B',
	fileName: 'b',
	category: 'typography',
	description: ['Bold component.'],
	features: ['Drop-in replacement for the native <p> element.', "Opts-in to your design system's typographic styles."],
	parts: [],
	usage: `<P>
	The two most popular science courses offered by the school are <B>chemistry</B>
	(the study of chemicals and the composition of substances) and <B>physics</B>
	(the study of the nature and properties of matter and energy).
</P>`,
	styling: `.b {
	@apply font-sans font-bold;
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const SpanRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<P>
				The two most popular science courses offered by the school are <B>chemistry</B> (the study of chemicals and the composition of substances) and <B>physics</B> (the study of the
				nature and properties of matter and energy).
			</P>
			<P>
				Notice how the <Code>{'<B>'}</Code> element is used to draw attention to text without indicating that it's more important.
			</P>
			<P className="mt-4">
				Notice that <Code>{'<b>'}</Code> tags will go inside <Code>{'<p>'}</Code> tags and inherit whatever typographical attributes that paragraph has.
			</P>
			<P className="mt-4">
				The <Code>{'<b>'}</Code> HTML element is used to draw the reader's attention to the element's contents, which are not otherwise granted special importance. This was formerly
				known as the Boldface element, and most browsers still draw the text in boldface. However, you should not use <Code>{'<b>'}</Code> for styling text because that's a job for CSS
				and you should not use <Code>{'<b>'}</Code> for granting importance or stress emphasis because that's a job for the <Code>{'<strong>'}</Code> and <Code>{'<em>'}</Code> tags,
				respectively.
			</P>
			<H2 className="mt-8">
				{'<b>'} vs {'<strong>'}
			</H2>
			<P>
				It is often confusing to new developers why there are so many ways to express the same thing on a rendered website. {'<b>'} and {'<strong>'} are perhaps one of the most common
				sources of confusion, causing developers to ask "Should I use {'<b>'} or {'<strong>'}? Don't they both do the same thing?"
			</P>
			<P className="mt-4">
				Not exactly. The {'<strong>'} element is for content that is of greater importance, while the {'<b>'} element is used to draw attention to text without indicating that it's more
				important.
			</P>
			<P className="mt-4">
				It may help to realize that both are valid and semantic elements in HTML and that it's a coincidence that they both have the same default styling (boldface) in most browsers
				(although some older browsers actually underline {'<strong>'}). Each element is meant to be used in certain types of scenarios, and if you want to bold text for decoration, you
				should instead actually use the CSS font-weight property.
			</P>
			<P className="mt-4">The intended meaning or purpose of the enclosed text should be what determines which element you use. Communicating meaning is what semantics are all about.</P>
			<Features features={features} />

			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default SpanRoute
